import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Tag, Plus, Pencil, Trash2, X, Check, Ban } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyForm = {
  code: '',
  description: '',
  discount_type: 'percentage',
  discount_value: '',
  min_order_value: '',
  max_discount: '',
  valid_from: '',
  valid_until: '',
  usage_limit: '',
  is_active: true,
};

const toIsoDate = (dt) => (dt ? new Date(dt).toISOString().slice(0, 10) : '');

const PromoCodeManagement = () => {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
  });

  const fetchPromos = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/promo-codes`, getAuthHeaders());
      setPromos(res.data);
    } catch (err) {
      console.error(err);
      toast({ title: 'Failed to load promo codes', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPromos(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (promo) => {
    setEditingId(promo.id);
    setForm({
      code: promo.code || '',
      description: promo.description || '',
      discount_type: promo.discount_type || 'percentage',
      discount_value: promo.discount_value?.toString() ?? '',
      min_order_value: promo.min_order_value?.toString() ?? '',
      max_discount: promo.max_discount != null ? promo.max_discount.toString() : '',
      valid_from: toIsoDate(promo.valid_from),
      valid_until: toIsoDate(promo.valid_until),
      usage_limit: promo.usage_limit != null ? promo.usage_limit.toString() : '',
      is_active: promo.is_active,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) {
      toast({ title: 'Code is required', variant: 'destructive' });
      return;
    }
    if (!form.discount_value || parseFloat(form.discount_value) <= 0) {
      toast({ title: 'Discount value must be greater than 0', variant: 'destructive' });
      return;
    }
    if (form.discount_type === 'percentage' && parseFloat(form.discount_value) > 100) {
      toast({ title: 'Percentage cannot exceed 100', variant: 'destructive' });
      return;
    }
    const payload = {
      code: form.code.trim().toUpperCase(),
      description: form.description || '',
      discount_type: form.discount_type,
      discount_value: parseFloat(form.discount_value),
      min_order_value: form.min_order_value ? parseFloat(form.min_order_value) : 0,
      max_discount: form.max_discount ? parseFloat(form.max_discount) : null,
      valid_from: form.valid_from ? new Date(form.valid_from).toISOString() : null,
      valid_until: form.valid_until ? new Date(`${form.valid_until}T23:59:59`).toISOString() : null,
      usage_limit: form.usage_limit ? parseInt(form.usage_limit, 10) : null,
      is_active: !!form.is_active,
    };
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/promo-codes/${editingId}`, payload, getAuthHeaders());
        toast({ title: 'Promo code updated' });
      } else {
        await axios.post(`${API}/admin/promo-codes`, payload, getAuthHeaders());
        toast({ title: 'Promo code created' });
      }
      setShowModal(false);
      fetchPromos();
    } catch (err) {
      toast({
        title: 'Save failed',
        description: err.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (promo) => {
    try {
      await axios.put(
        `${API}/admin/promo-codes/${promo.id}`,
        { is_active: !promo.is_active },
        getAuthHeaders()
      );
      toast({ title: `Promo ${!promo.is_active ? 'activated' : 'deactivated'}` });
      fetchPromos();
    } catch (err) {
      toast({ title: 'Update failed', variant: 'destructive' });
    }
  };

  const deletePromo = async (promo) => {
    if (!window.confirm(`Delete promo code "${promo.code}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/promo-codes/${promo.id}`, getAuthHeaders());
      toast({ title: 'Promo deleted' });
      fetchPromos();
    } catch (err) {
      toast({ title: 'Delete failed', variant: 'destructive' });
    }
  };

  const formatDate = (dt) => (dt ? new Date(dt).toLocaleDateString() : '—');

  const statusPill = (promo) => {
    const now = new Date();
    if (!promo.is_active) return <span className="px-2 py-0.5 rounded-full text-xs bg-gray-200 text-gray-700">Inactive</span>;
    if (promo.valid_until && new Date(promo.valid_until) < now)
      return <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">Expired</span>;
    if (promo.usage_limit != null && promo.usage_count >= promo.usage_limit)
      return <span className="px-2 py-0.5 rounded-full text-xs bg-orange-100 text-orange-700">Used up</span>;
    return <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">Active</span>;
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4 sm:p-6" data-testid="promo-management">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
            <Tag className="w-6 h-6 text-yellow-500" />
            Promo Codes
          </h2>
          <p className="text-sm text-gray-500 mt-1">Create and manage discount codes for checkout</p>
        </div>
        <button
          onClick={openCreate}
          data-testid="promo-add-btn"
          className="inline-flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-4 py-2 rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Promo Code
        </button>
      </div>

      {loading ? (
        <p className="text-center py-8 text-gray-500">Loading...</p>
      ) : promos.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
          <Tag className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No promo codes yet</p>
          <p className="text-xs text-gray-400 mt-1">Click &quot;Add Promo Code&quot; to create your first offer</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="promo-table">
            <thead className="bg-gray-50 border-b">
              <tr className="text-left text-gray-600">
                <th className="py-3 px-3">Code</th>
                <th className="py-3 px-3">Discount</th>
                <th className="py-3 px-3">Min. Order</th>
                <th className="py-3 px-3">Validity</th>
                <th className="py-3 px-3">Usage</th>
                <th className="py-3 px-3">Status</th>
                <th className="py-3 px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {promos.map((p) => (
                <tr key={p.id} className="border-b hover:bg-gray-50" data-testid={`promo-row-${p.code}`}>
                  <td className="py-3 px-3">
                    <div className="font-mono font-bold text-gray-900">{p.code}</div>
                    {p.description && <div className="text-xs text-gray-500 mt-0.5 max-w-xs truncate">{p.description}</div>}
                  </td>
                  <td className="py-3 px-3">
                    {p.discount_type === 'percentage'
                      ? <span className="font-semibold">{p.discount_value}% off</span>
                      : <span className="font-semibold">₹{p.discount_value} off</span>}
                    {p.max_discount != null && <div className="text-xs text-gray-500">up to ₹{p.max_discount}</div>}
                  </td>
                  <td className="py-3 px-3">{p.min_order_value ? `₹${p.min_order_value}` : '—'}</td>
                  <td className="py-3 px-3 text-xs">
                    <div>From: {formatDate(p.valid_from)}</div>
                    <div>To: {formatDate(p.valid_until)}</div>
                  </td>
                  <td className="py-3 px-3">
                    <span className="font-semibold">{p.usage_count}</span>
                    <span className="text-gray-500"> / {p.usage_limit ?? '∞'}</span>
                  </td>
                  <td className="py-3 px-3">{statusPill(p)}</td>
                  <td className="py-3 px-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        onClick={() => toggleActive(p)}
                        title={p.is_active ? 'Deactivate' : 'Activate'}
                        data-testid={`promo-toggle-${p.code}`}
                        className={`p-2 rounded-lg transition-colors ${p.is_active ? 'text-orange-600 hover:bg-orange-50' : 'text-green-600 hover:bg-green-50'}`}
                      >
                        {p.is_active ? <Ban className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => openEdit(p)}
                        title="Edit"
                        data-testid={`promo-edit-${p.code}`}
                        className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deletePromo(p)}
                        title="Delete"
                        data-testid={`promo-delete-${p.code}`}
                        className="p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white">
              <h3 className="text-lg font-bold">{editingId ? 'Edit Promo Code' : 'Create Promo Code'}</h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-1 rounded-full hover:bg-gray-100"
                data-testid="promo-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4" data-testid="promo-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">Code *</label>
                  <input
                    type="text"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    required
                    placeholder="SAVE10"
                    data-testid="promo-form-code"
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 font-mono uppercase tracking-wider"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">Discount Type *</label>
                  <select
                    value={form.discount_type}
                    onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                    data-testid="promo-form-type"
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed amount (₹)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">
                    Discount Value * {form.discount_type === 'percentage' ? '(0–100)' : '(₹)'}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.discount_value}
                    onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                    required
                    data-testid="promo-form-value"
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
                {form.discount_type === 'percentage' && (
                  <div>
                    <label className="block text-sm font-semibold mb-1">Max Discount (₹, optional)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.max_discount}
                      onChange={(e) => setForm({ ...form, max_discount: e.target.value })}
                      placeholder="e.g. 500"
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-semibold mb-1">Min. Order Value (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.min_order_value}
                    onChange={(e) => setForm({ ...form, min_order_value: e.target.value })}
                    data-testid="promo-form-min-order"
                    placeholder="0"
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">Usage Limit (optional)</label>
                  <input
                    type="number"
                    min="1"
                    value={form.usage_limit}
                    onChange={(e) => setForm({ ...form, usage_limit: e.target.value })}
                    placeholder="Unlimited if empty"
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">Valid From (optional)</label>
                  <input
                    type="date"
                    value={form.valid_from}
                    onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">Valid Until (optional)</label>
                  <input
                    type="date"
                    value={form.valid_until}
                    onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Description (optional)</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="e.g. New Year offer"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
              <label className="flex items-center gap-2 select-none">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <span className="text-sm">Active (customers can use this code)</span>
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 rounded-lg border hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="promo-form-submit"
                  className="px-4 py-2 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-black font-semibold disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoCodeManagement;
